import { useEffect, useRef } from "react";

import { Stack } from "@mui/material";
import {
  ColumnDirective,
  ColumnsDirective,
  ExcelExport,
  ForeignKey,
  GridComponent,
  Group,
  Inject,
  Page,
  Sort,
  Toolbar,
} from "@syncfusion/ej2-react-grids";
import { useTranslation } from "react-i18next";

import { NoRecordsFound } from "../../atoms/NoRecordsFound/NoRecordsFound";
import CircularSpinner from "../../atoms/Spinner/CircularSpinner/CircularSpinner";
import { Heading5 } from "../../atoms/Typography/Heading/Heading";
import { PaginationGrid } from "../../molecules/Pagination/PaginationGrid";
import { PostPerPage } from "../../molecules/Pagination/PostPerPage";
import "./BasicGrid.style.css";

export const BasicGrid = ({
  data,
  sortingOptions,
  groupOptions,
  customSearchText,
  customizeCell,
  columnArray,
  allowGrouping,
  allowSorting,
  allowPaging,
  field,
  rowSelect,
  onRowClick,
  pageData,
  paginationClick,
  pageSizeChange,
  resetCurrentPage,
  isLoading, // indicates whether api call has returned the response
  isError, // indicates whether api call has produced an error
  excelQueryCellInfo,
  gridInstance,
  noRecFoundMessage,
  customHeaderCell,
}) => {
  const { t } = useTranslation();
  let gridRef = useRef();
  const searchSettings = {
    fields: field,
  };

  useEffect(() => {
    customSearchText && gridRef.current.search(customSearchText);
  }, [customSearchText]);

  const gridDTemplate = (props) => {
    return (
      <Stack
        justifyContent={"center"}
        alignItems={props.headerTextAlign || "flex-start"}
        height={"100%"}
      >
        <Heading5 text={props.headerText} />
      </Stack>
    );
  };

  const headerCellInfo = (args) => {
    // if the column field is mapped to a value to sort then show custom icon
    if (args.cell.column.field !== "actions" || !args.cell.column.field) {
      args.node.classList.add("customicon");
    } else {
      // if the column field is "actions" or not given a value to sort then avoid custom icon
      args.node.classList.add("avoidcustomicon");
    }
  };

  const handlePageSizeChange = (selectedPageSize) => {
    pageSizeChange(selectedPageSize);
  };

  const handlePaginationClick = (pageNumber) => {
    paginationClick(pageNumber);
  };

  return (
    <div
      style={{
        height: "100%",
        padding: isLoading && "20%",
      }}
    >
      {/* show spinner until api call has ended */}
      {isLoading && <CircularSpinner />}
      {/* show error if data is not available or error has occured */}
      {((isLoading === false && !data?.length) ||
        (isLoading === undefined && !data?.length)) && (
        <NoRecordsFound
          label={noRecFoundMessage || t("validation:no-records-found")}
          isError={isError}
        />
      )}
      {/* show table if loading has ended and data is available */}
      {data?.length > 0 && !isLoading && (
        <GridComponent
          recordClick={(e) => rowSelect && onRowClick(e.rowData)}
          selectionSettings={rowSelect && { type: "Single", mode: "Row" }}
          queryCellInfo={customizeCell ? customizeCell : null}
          dataSource={data}
          allowExcelExport={true}
          allowGrouping={allowGrouping}
          allowSorting={allowSorting}
          headerCellInfo={headerCellInfo}
          allowTextWrap={true}
          gridLines="Both"
          enableHover={false}
          allowSelection={false}
          allowResizing={true}
          groupSettings={groupOptions ? groupOptions : null}
          sortSettings={sortingOptions ? sortingOptions : null}
          searchSettings={field && searchSettings}
          ref={(g) => {
            gridRef.current = g;
            gridInstance && gridInstance(g);
          }}
          height="auto"
          excelQueryCellInfo={excelQueryCellInfo}
        >
          <ColumnsDirective>
            {columnArray.map((item, i) => {
              return (
                <ColumnDirective
                  key={i}
                  headerText={item.fieldtext}
                  width={item?.width}
                  textAlign={item.textAlign}
                  headerTextAlign={item.headerTextAlign}
                  field={item?.field || null}
                  template={item.template || null}
                  headerTemplate={customHeaderCell?customHeaderCell:gridDTemplate}
                  enableGroupByFormat={item.enableGroupByFormat}
                  minWidth={item?.minWidth}
                  autoFit={true}
                />
              );
            })}
          </ColumnsDirective>
          <Inject
            services={[Page, Group, Sort, ForeignKey, ExcelExport, Toolbar]}
          />
        </GridComponent>
      )}
      {/* show pagination if loading has ended and data is available and pagination is allowed */}
      {allowPaging && !isLoading && data?.length > 0 && (
        <Stack
          mt={"22px"}
          gap={"16px"}
          width={"100%"}
          direction={"row"}
          justifyContent={"space-between"}
        >
          <PostPerPage
            onPageSizeChange={handlePageSizeChange}
            pageSize={pageData?.pageSize}
          />
          <PaginationGrid
            pagerData={pageData}
            resetCurrentPage={resetCurrentPage}
            onPageChange={handlePaginationClick}
          />
        </Stack>
      )}
    </div>
  );
};
